import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { getGoalInfoForCoursesPage } from '@selectors/syllabus';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import Paper from '@base/Paper/Paper';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import breadcrumbs from '@utils/breadcrumbs';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import pushRoute from '@utils/pushRoute';
import Image from '@base/Image';
import { getIsGTPGoal } from '@api/selectors/constants';
import EducatorAvatar from '@cont/EducatorProfile/EducatorAvatar';
import P1 from '@base/P1/P1';
import { getFullName } from '@api/hooks/utils';
import useConstants from '@api/hooks/useConstants';
import { getIsSubscribedToOffline } from '@selectors/subscription';
import { emptyObject } from '@constants/empty';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import { getIsSyllabusPageEducatorSpecific } from '../utils';
import getTopicGroupAssetObj from '../constants/getTopicGroupAssetObj';

const HeaderNavBar = styled(Paper)`
  margin-bottom: ${({
    // @ts-ignore
    $margin
  }) => $margin};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_12B};
  position: sticky;
  z-index: 1;
  top: 0;
  ${mediaQuery.uptoMobile} {
    background: var(--color-base-0);
    box-shadow: none;
    padding: ${Spacings.SPACING_6B} 0 0 0;
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_6B};
`;

const Title = styled(H2)`
  font-weight: ${Fonts.BOLD};
  height: ${Spacings.SPACING_12B};
  line-height: 150%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${mediaQuery.uptoMobile} {
    height: auto;
  }
`;

const Wrapper = styled.div`
  margin-left: ${Spacings.SPACING_4B};
`;

const StyledP1 = styled(P1)`
  color: var(--color-text-secondary);
`;

interface IHeaderProps {
  goalUID: string;
  title: string;
  addMargin: boolean;
  topicGroupTitle: string;
  author: any;
}

const Header: React.FC<IHeaderProps> = ({
  goalUID,
  title,
  addMargin,
  topicGroupTitle,
  author
}) => {
  const {
    query: { educatorUID, educatorUsername }
  } = useRouter();
  const isSyllabusPageEducatorSpecific = getIsSyllabusPageEducatorSpecific(
    educatorUID,
    author
  );
  const isRecordedGoal = useIsRecordedGoal(goalUID);

  const getHeaderTags = useCallback(
    (goalTopology, isGTP, isOfflineSubscription) => {
      let tags = [];
      if (goalTopology) {
        tags = tags.concat(
          ...breadcrumbs.getPlusTopicsTags(goalTopology, isOfflineSubscription)
        );
        if (isSyllabusPageEducatorSpecific) {
          tags = tags.concat(
            ...breadcrumbs.getEducatorTag(author, educatorUsername)
          );
        } else {
          tags = tags.concat(...breadcrumbs.getTopics(goalTopology, isGTP));
        }
      }
      return tags;
    },
    [author, educatorUsername, isSyllabusPageEducatorSpecific]
  );

  const goalTopology = useSelector((state) =>
    // @ts-ignore
    getGoalInfoForCoursesPage(state, goalUID)
  );
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const {
    query: { goalSlug }
  } = useRouter();
  const { icon } = getTopicGroupAssetObj(topicGroupTitle);

  const goBack = () => {
    if (isSyllabusPageEducatorSpecific)
      pushRoute('/[authorID]', `/@${educatorUsername}`);
    else
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/topics',
        `/goal/${goalSlug}/${goalUID}/topics`
      );
  };
  const {
    levelInfo = {},
    avatar: thumbnail,
    firstName,
    lastName
  } = author || emptyObject;

  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));
  return (
    <>
      <HeaderNavBar
        // @ts-ignore
        $margin={addMargin ? Spacings.SPACING_10B : ''}
      >
        <BreadCrumbs
          tags={getHeaderTags(
            goalTopology,
            isGTP || isRecordedGoal,
            isOfflineSubscription
          )}
          onBackClick={goBack}
          useBackClick
        />
        <TitleWrapper>
          {isSyllabusPageEducatorSpecific ? (
            // @ts-ignore
            <EducatorAvatar size="M" levelInfo={levelInfo} src={thumbnail} />
          ) : (
            <Image
              height={Spacings.SPACING_10B}
              width={Spacings.SPACING_10B}
              alt="syllabus-subject-icon"
              src={icon}
            />
          )}
          <Wrapper>
            <Title as="h1" data-testid="topic-title">
              {title}
            </Title>
            {isSyllabusPageEducatorSpecific && (
              <StyledP1 data-testid="educator-name">
                By {getFullName(firstName, lastName)}
              </StyledP1>
            )}
          </Wrapper>
        </TitleWrapper>
      </HeaderNavBar>
    </>
  );
};

export default Header;
